import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { config } from '@uhg-abyss/web/tools/config';
import { storage } from '@uhg-abyss/web/tools/storage';
import { FeatureToggle } from '@sde/widgets-core/src/common/FeatureToggle';
import { event } from '@uhg-abyss/web/tools/event';
import { NavMenuPrimitives } from '@uhg-abyss/web/ui/NavMenu';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { useOverlay } from '@uhg-abyss/web/hooks/useOverlay';
import { usePageCollapsingHeader } from '@src/state/pageCollapsingHeader';
import { useStateRouterContext } from '@uhg-abyss/web/ui/StateRouter';
import { useModal } from '@src/state/modal';
import { ReactivateTrainingTipsNew } from './ReactivateTrainingTipsNew';
import { defaultEmptyFunction } from './HeaderSections/config';
import { handleLogout } from '@src/tools/handleLogout';

const StyledCell = styled(NavMenuPrimitives.MenuItem, {
  '& .abyss-nav-menu-link': {
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '4px 8px',
    '&:hover': {
      backgroundColor: '$gray2',
    },
  },
});

const NavMenuPrimitivesMenuItem = ({ datatestid, title, onClick }) => {
  return (
    <div data-testId={datatestid} data-cy={datatestid}>
      <StyledCell title={title} onClick={onClick} />
    </div>
  );
};

const StyledNavMenuPrimitivesColumns = styled(NavMenuPrimitives.Columns, {
  // eslint-disable-next-line consistent-return
  dynamic: ({ isPageCollapsingHeaderCssStyle }) => {
    if (isPageCollapsingHeaderCssStyle) {
      return {
        '&.abyss-nav-menu-columns-root': {
          display: 'flex',
          margin: '0px',
          width: '100%',
          listStyle: 'none',
          '@media (min-width: 984px)': {
            flexDirection: 'column !important',
            padding: '$sm $lg !important',
          },
        },
      };
    }
  },
});

const StyledNavMenuPrimitivesColumn = styled(NavMenuPrimitives.Column, {
  // eslint-disable-next-line consistent-return
  dynamic: ({ isPageCollapsingHeaderCssStyle }) => {
    if (isPageCollapsingHeaderCssStyle) {
      return {
        '&.abyss-nav-menu-column-root': {
          display: 'flex',
          margin: '0px',
          width: '100%',
          listStyle: 'none',
          '@media (min-width: 984px)': {
            borderLeft: '0px !important',
            flexDirection: 'column !important',
            padding: '0px !important',
          },
        },
      };
    }
  },
});

const TagContainer = styled('div', {
  display: 'inline-flex',
  gap: '5px',
  alignItems: 'center',
});

const ExternalIconContainer = styled('div', {
  display: 'inline-flex',
});

export const MyAccountNew = () => {
  const { user } = useUser.state();
  const { provider } = user;
  const router = useRouter();
  const { toggleModal } = useModal.actions();
  const flyout = useOverlay('pageheader-model');

  const tailoredTrainingPermission = Object.keys(provider).length !== 0;

  const handleEditMode = () => {
    storage.session.set('accountInformationEditMode', 'off');
    storage.session.set('communicationPreferenceEditMode', 'off');
    storage.session.set('trainingPreferenceEditMode', 'off');
    storage.session.set('priorAuthAppealEditMode', 'off');
    storage.session.set('claimAppealEditMode', 'off');
    storage.session.set('pendedTicketEditMode', 'off');
    storage.session.set('pendingClaimsEditMode', 'off');
    storage.session.set('reconsiderationEditMode', 'off');
    storage.session.set('smartEditsEditMode', 'off');
    storage.session.set('priorAuthEditMode', 'off');
    storage.session.set('refferalEditMode', 'off');
    storage.session.set('admissionEditMode', 'off');
    storage.session.set('dischargeEditMode', 'off');
    storage.session.set('emergencyEditMode', 'off');
    storage.session.set('ppcTrackitEditMode', 'off');
    storage.session.set('requiredCommunicationPreferenceEdits', 'off');
  };
  const handleExitLink = (linkText, href) => {
    if (href) {
      window.location.replace(href);
    }
    if (linkText === 'Organization and Tax ID Access') {
      event('EXIT_LINK_NAVIGATE', { linkText });
    } else {
      event('PROFILE_SETTING_LINKS', { linkText });
    }
    handleEditMode();
    flyout.close();
  };

  const handleExitLinkForRequestAccess = (linkText, href) => {
    window.open(href, '_blank');
    if (linkText === 'Organization and Tax ID Access') {
      event('EXIT_LINK_NAVIGATE', { linkText });
    } else {
      event('PROFILE_SETTING_LINKS', { linkText });
    }
    handleEditMode();
    flyout.close();
  };
  const columnTitle = getFeature('PREFERENCE_CENTER')
    ? 'Profile & Settings'
    : null;

  const openPermissionsModal = useCallback((toolName) => {
    toggleModal({
      key: 'permissions',
      isOpen: true,
      toolName,
    });
  }, []);

  const [isPageCollapsingHeaderStyle, setIsPageCollapsingHeaderStyle] =
    useState(false);

  const { showPageCollapsingHeader } = usePageCollapsingHeader.state();

  useEffect(() => {
    if (showPageCollapsingHeader) {
      setIsPageCollapsingHeaderStyle(true);
    } else {
      setIsPageCollapsingHeaderStyle(false);
    }
  }, [showPageCollapsingHeader]);

  const { methods } = useStateRouterContext() || {
    methods: { goBack: defaultEmptyFunction },
  };

  useEffect(() => {
    return () => {
      methods.goBack();
    };
  }, []);

  const logout = (linkText) => {
    handleLogout();
    event('PROFILE_SETTING_LINKS', { linkText });
  };

  return (
    <StyledNavMenuPrimitivesColumns
      isPageCollapsingHeaderCssStyle={isPageCollapsingHeaderStyle}
    >
      <StyledNavMenuPrimitivesColumn
        title={columnTitle}
        isPageCollapsingHeaderCssStyle={isPageCollapsingHeaderStyle}
      >
        <FeatureToggle toggle='PREFERENCE_CENTER'>
          <NavMenuPrimitivesMenuItem
            datatestid='top-bar-pref-center-link'
            title='Account Information'
            onClick={() =>
              handleExitLink('Account Information', '#/prefcenter')
            }
          />
          <FeatureToggle toggle='ORG_TAXID_ACCESS_NAV_ITEM'>
            <NavMenuPrimitivesMenuItem
              datatestid='top-bar-pref-center-org-tax-link'
              title='Organization & Tax ID Access'
              onClick={() =>
                handleExitLink(
                  'Organization & Tax ID Access',
                  '#/prefcenter/orgtaxIdAccess'
                )
              }
            />
          </FeatureToggle>
          <FeatureToggle toggle='COMM_PREF_TOGGLE'>
            <NavMenuPrimitivesMenuItem
              datatestid='top-bar-pref-center-communication-pref-link'
              title='Communication Preferences'
              onClick={() =>
                handleExitLink(
                  'Communication Preferences',
                  '#/prefcenter/comm-pref'
                )
              }
            />
          </FeatureToggle>
          <FeatureToggle toggle="PPC_PAYMENT_PREFERENCES">
            <NavMenuPrimitivesMenuItem
              datatestid="top-bar-pref-center-payment-preferences-link"
              title="Payment Preferences"
              onClick={() =>
                handleExitLink(
                  'Payment Preferences',
                  '#/prefcenter/paymentPreferences'
                )
              }
            />
          </FeatureToggle>
          <FeatureToggle toggle="TRAINING_LIBRARY_TOGGLE">
            <NavMenuPrimitivesMenuItem
              datatestid='top-bar-pref-center-training-library-link'
              title='Tailored Training'
              href={
                tailoredTrainingPermission ? '/prefcenter/tailoredtraining' : ''
              }
              onClick={
                tailoredTrainingPermission
                  ? handleExitLink('Tailored Training')
                  : openPermissionsModal('Tailored Training')
              }
            />
          </FeatureToggle>
          <FeatureToggle toggle='PREF_CENTER_TRAININGS'>
            <NavMenuPrimitivesMenuItem
              datatestid='top-bar-pref-center-trainings-link'
              title='Training Preferences'
              onClick={() =>
                handleExitLink('Training', '#/prefcenter/trainings')
              }
            />
          </FeatureToggle>
        </FeatureToggle>
        <FeatureToggle toggle='REQUEST_ACCESS_DS'>
          <NavMenuPrimitivesMenuItem
            datatestid='top-bar-request-access-link'
            title={
              <TagContainer>
                Request Access
                <ExternalIconContainer>
                  <IconMaterial icon='launch' size='16px' />
                </ExternalIconContainer>
              </TagContainer>
            }
            onClick={() =>
              handleExitLinkForRequestAccess(
                'Request Access',
                `${config('DIGITAL_SECURITY_URL')}`
              )
            }
          />
        </FeatureToggle>
        <ReactivateTrainingTipsNew />
        <NavMenuPrimitivesMenuItem
          datatestid='top-bar-sign-out-link'
          title='Sign Out'
          onClick={() => logout('Sign Out')}
        />
      </StyledNavMenuPrimitivesColumn>
    </StyledNavMenuPrimitivesColumns>
  );
};

NavMenuPrimitivesMenuItem.propTypes = {
  datatestid: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
};

NavMenuPrimitivesMenuItem.defaultProps = {
  datatestid: null,
  title: null,
  onClick: () => { },
};
